import React, { useCallback, useState } from 'react';
import { useQuery } from "react-query";
import axios, { AxiosResponse } from "axios";
import { PendingDataGuard } from "../../commons/Indicators";
import { Button, Descriptions, Divider, Flex, Form, Image, Input, List, message, Modal, Spin, Typography, Collapse } from "antd";
import { EditOutlined, UserOutlined, TeamOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import { defaultFormItemLayout } from "../../commons/forms";
import DescriptionsItem from "antd/es/descriptions/Item";
import AddButton from "../../commons/AddButton";
import { EnsureLoggedIn } from "../EnsureLoggedIn";
import { useTranslation } from "react-i18next";
import { AdminImageChooser } from "../../commons/AdminImageChooser";
import { OrganizerDto, UserSummary } from "../../commons/data/types";
import { LinkEditor } from "../../commons/LinkEditor";
import { UserSearchSelect } from "../../commons/UserSearchSelect";

export const OrganizersAdministration = () => {
    const { t } = useTranslation();
    const [form] = useForm<OrganizerDto>();
    const [editing, setEditing] = useState<boolean>(false);
    const [working, setWorking] = useState<boolean>(false);

    // Modal state
    const [ownerModalOpen, setOwnerModalOpen] = useState<boolean>(false);
    const [gmModalOpen, setGmModalOpen] = useState<boolean>(false);
    const [selectedOrganizer, setSelectedOrganizer] = useState<OrganizerDto | null>(null);

    const { data: organizers, isLoading, isError, refetch } = useQuery(
        'admin-organizers',
        () => axios.get<object, AxiosResponse<OrganizerDto[]>>(`/api/admin/organizers`)
            .then(r => r.data)
    );

    const doSubmit = useCallback((values: OrganizerDto) => {
        setWorking(true);
        const request = !values.id
            ? axios.post<object, AxiosResponse<OrganizerDto>>(`/api/admin/organizers`, values)
            : axios.put<object, AxiosResponse<OrganizerDto>>(`/api/admin/organizers/${values.id}`, values);

        request
            .then(() => {
                refetch();
                message.success('Success');
                form.resetFields();
                setEditing(false);
            })
            .catch(() => message.error('Error saving organizer'))
            .finally(() => setWorking(false));
    }, [form, refetch]);

    const handleSetOwner = useCallback((user: UserSummary) => {
        if (!selectedOrganizer) return;
        setWorking(true);
        axios.post<object, AxiosResponse<OrganizerDto>>(`/api/admin/organizers/${selectedOrganizer.id}/owner`, {
            ownerId: user.id
        })
            .then(() => {
                refetch();
                message.success('Owner set successfully');
                setOwnerModalOpen(false);
                setSelectedOrganizer(null);
            })
            .catch((err) => {
                const errMsg = err.response?.data || 'Error setting owner';
                message.error(errMsg);
            })
            .finally(() => setWorking(false));
    }, [selectedOrganizer, refetch]);

    const handleAddGameMaster = useCallback((user: UserSummary) => {
        if (!selectedOrganizer) return;
        setWorking(true);
        axios.post<object, AxiosResponse<OrganizerDto>>(`/api/admin/organizers/${selectedOrganizer.id}/game-masters`, {
            gameMasterId: user.id
        })
            .then(() => {
                refetch();
                message.success('Game Master added successfully');
                setGmModalOpen(false);
                setSelectedOrganizer(null);
            })
            .catch((err) => {
                const errMsg = err.response?.data || 'Error adding Game Master';
                message.error(errMsg);
            })
            .finally(() => setWorking(false));
    }, [selectedOrganizer, refetch]);

    const handleRemoveGameMaster = useCallback((organizerId: string, gameMasterId: string) => {
        setWorking(true);
        axios.delete<object, AxiosResponse<OrganizerDto>>(`/api/admin/organizers/${organizerId}/game-masters/${gameMasterId}`)
            .then(() => {
                refetch();
                message.success('Game Master removed successfully');
            })
            .catch((err) => {
                const errMsg = err.response?.data || 'Error removing Game Master';
                message.error(errMsg);
            })
            .finally(() => setWorking(false));
    }, [refetch]);

    return (
        <EnsureLoggedIn shouldBeAdmin={true}>
            <div style={{ backgroundColor: 'white', padding: '1em' }}>
                <PendingDataGuard data={organizers} isLoading={isLoading} isError={isError}>
                    {working && <Spin fullscreen tip={'Working...'} />}
                    <Modal open={editing} closable destroyOnHidden={false} onCancel={() => {
                        setEditing(false);
                        form.resetFields();
                    }} footer={null}>
                        <div style={{ height: '25px' }} />
                        <Form form={form} {...defaultFormItemLayout} onFinish={doSubmit}>
                            <Form.Item name={'id'} hidden />
                            <Form.Item name={'name'} label={'Name'} required rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name={'description'} label={'Description'} required rules={[{ required: true }]}>
                                <Input.TextArea rows={4} />
                            </Form.Item>
                            <Form.Item name={'logoId'} label={'Logo'} required rules={[{ required: true }]}>
                                <AdminImageChooser category={'ADMIN'} />
                            </Form.Item>
                            <Divider>Links</Divider>
                            <Form.Item key={'website'} name={`website`} label={'Website'}>
                                <LinkEditor type={"Website"} />
                            </Form.Item>
                            <Form.Item key={'facebook'} name={`facebook`} label={'Facebook'}>
                                <LinkEditor type={"Facebook"} />
                            </Form.Item>
                            <Form.Item key={'instagram'} name={`instagram`} label={'Instagram'}>
                                <LinkEditor type={"Instagram"} />
                            </Form.Item>
                            <Form.Item key={'tiktok'} name={`tiktok`} label={'TikTok'}>
                                <LinkEditor type={"TikTok"} />
                            </Form.Item>
                            <Flex vertical={false} justify={'end'}>
                                <Button type={"primary"} onClick={form.submit}>Save</Button>
                            </Flex>
                        </Form>
                    </Modal>

                    {/* Owner Modal */}
                    <Modal
                        title={`Set Owner for ${selectedOrganizer?.name}`}
                        open={ownerModalOpen}
                        onCancel={() => {
                            setOwnerModalOpen(false);
                            setSelectedOrganizer(null);
                        }}
                        footer={null}
                        destroyOnClose
                    >
                        <UserSearchSelect onlyNarrators={false} onSelect={handleSetOwner} />
                    </Modal>

                    {/* Add GM Modal */}
                    <Modal
                        title={`Add Game Master to ${selectedOrganizer?.name}`}
                        open={gmModalOpen}
                        onCancel={() => {
                            setGmModalOpen(false);
                            setSelectedOrganizer(null);
                        }}
                        footer={null}
                        destroyOnClose
                    >
                        <UserSearchSelect onlyNarrators={true} onSelect={handleAddGameMaster} />
                    </Modal>

                    <List
                        header={<AddButton onSave={() => {
                            form.resetFields();
                            setEditing(true);
                        }} />}
                        size={"small"}
                        dataSource={organizers || []}
                        style={{ maxWidth: '100%' }}
                        renderItem={(organizer) => (
                            <div style={{ maxWidth: '100%' }}>
                                <Descriptions bordered size={"small"} title={organizer.name} layout={"vertical"}>
                                    <DescriptionsItem label={<Typography.Text strong>Description</Typography.Text>}>
                                        <Typography.Paragraph>{organizer.description}</Typography.Paragraph>
                                    </DescriptionsItem>
                                    <DescriptionsItem label={<Typography.Text strong>Logo</Typography.Text>}>
                                        <Flex justify={'center'}>
                                            <Image preview src={organizer.logoUrl} style={{ maxHeight: '100px' }} />
                                        </Flex>
                                    </DescriptionsItem>
                                    <DescriptionsItem label={<Typography.Text strong>Links</Typography.Text>}>
                                        <ul>
                                            {organizer.website && <li key={organizer.website.id}><a href={organizer.website.url} target="_blank" rel="noopener noreferrer">{organizer.website.type}</a></li>}
                                            {organizer.facebook && <li key={organizer.facebook.id}><a href={organizer.facebook.url} target="_blank" rel="noopener noreferrer">{organizer.facebook.type}</a></li>}
                                            {organizer.instagram && <li key={organizer.instagram.id}><a href={organizer.instagram.url} target="_blank" rel="noopener noreferrer">{organizer.instagram.type}</a></li>}
                                            {organizer.tiktok && <li key={organizer.tiktok.id}><a href={organizer.tiktok.url} target="_blank" rel="noopener noreferrer">{organizer.tiktok.type}</a></li>}
                                        </ul>
                                    </DescriptionsItem>
                                    <DescriptionsItem label={<Typography.Text strong>Owner</Typography.Text>}>
                                        <Typography.Text>
                                            {organizer.owner ? `${organizer.owner.name} (${organizer.owner.email})` : 'No owner assigned'}
                                        </Typography.Text>
                                    </DescriptionsItem>
                                    <DescriptionsItem>
                                        <Flex vertical gap="small" style={{ width: '100%' }}>
                                            <Button
                                                type={"primary"}
                                                icon={<EditOutlined />}
                                                onClick={() => {
                                                    form.setFieldsValue(organizer);
                                                    setEditing(true);
                                                }}
                                                size={"large"}
                                                style={{ width: '100%' }}
                                            >
                                                {t('common.edit')}
                                            </Button>
                                            <Button
                                                type={"default"}
                                                icon={<UserOutlined />}
                                                onClick={() => {
                                                    setSelectedOrganizer(organizer);
                                                    setOwnerModalOpen(true);
                                                }}
                                                size={"large"}
                                                style={{ width: '100%' }}
                                            >
                                                Set Owner
                                            </Button>
                                        </Flex>
                                    </DescriptionsItem>
                                </Descriptions>

                                <Collapse style={{ marginTop: '8px' }}>
                                    <Collapse.Panel
                                        header={
                                            <span style={{ fontWeight: 'bold' }}>
                                                <TeamOutlined style={{ marginRight: '8px' }} />
                                                Game Masters ({organizer.gameMasters?.length || 0})
                                            </span>
                                        }
                                        key="game-masters"
                                    >
                                        <List
                                            size="small"
                                            dataSource={organizer.gameMasters || []}
                                            renderItem={gm => (
                                                <List.Item
                                                    actions={[
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleRemoveGameMaster(organizer.id!, gm.id)}
                                                        />
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        title={gm.name || gm.nickname || "No Name"}
                                                        description={gm.email}
                                                    />
                                                </List.Item>
                                            )}
                                            footer={
                                                <Button
                                                    type="dashed"
                                                    block
                                                    icon={<PlusOutlined />}
                                                    onClick={() => {
                                                        setSelectedOrganizer(organizer);
                                                        setGmModalOpen(true);
                                                    }}
                                                >
                                                    Add Game Master
                                                </Button>
                                            }
                                        />
                                    </Collapse.Panel>
                                </Collapse>
                                <Divider />
                            </div>)}
                    />
                </PendingDataGuard>
            </div>
        </EnsureLoggedIn>
    );
};